import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import http from '@/services/http';
import { isCatalogPurgePending } from '@/composables/api/useRawDdexPartyExclusions';
import type { RawDdexExclusionUserRef, RawDdexPartyExclusion, RawDdexPartyRef } from '@/composables/api/useRawDdexPartyExclusions';

/**
 * A per-sender DDEX ingest configuration: the sender (distributor) we ingest from, its gated S3
 * import path, and an enabled toggle that drives the hourly ddex:auto-process schedule. Exclusions
 * for the sender are surfaced inline (loaded on the show endpoint, and on index trimmed to the
 * sent_on_behalf_of party) for the per-party view.
 */
export interface DdexConfiguration {
  id: number;
  raw_ddex_party_sender_id: number;
  s3_import_path: string;
  enabled: boolean;
  sender: RawDdexPartyRef | null;
  created_by_user: RawDdexExclusionUserRef | null;
  exclusions?: RawDdexPartyExclusion[];
  created_at: string | null;
  updated_at: string | null;
}

export interface DdexConfigurationInput {
  raw_ddex_party_sender_id: number;
  s3_import_path: string;
  enabled?: boolean;
}

export interface DdexConfigurationUpdateInput {
  s3_import_path?: string;
  enabled?: boolean;
}

const RESOURCE_BASE = '/api/ddex_configuration';
export const DDEX_CONFIGURATIONS_QUERY_KEY = ['ddexConfigurations'] as const;

interface ListEnvelope<T> {
  data: T[];
}

interface ItemEnvelope<T> {
  data: T;
}

/** True while any config has an exclusion with a purge in flight, so the list should keep polling. */
function hasActiveCatalogPurgePoll(configurations: DdexConfiguration[] | undefined): boolean {
  return configurations?.some(
    (configuration) => (configuration.exclusions ?? []).some(isCatalogPurgePending),
  ) ?? false;
}

export function useDdexConfigurations() {
  return useQuery({
    queryKey: DDEX_CONFIGURATIONS_QUERY_KEY,
    queryFn: async (): Promise<DdexConfiguration[]> => {
      const response = await http.get<ListEnvelope<DdexConfiguration>>(RESOURCE_BASE);
      return response.data?.data ?? [];
    },
    // Poll while a catalog purge is in flight so "Purging catalog…" advances to "Catalog purged"
    // without a manual reload (parity with the old exclusions page).
    refetchInterval: (query) => (hasActiveCatalogPurgePoll(query.state.data) ? 3000 : false),
  });
}

export function useCreateDdexConfiguration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: DdexConfigurationInput) => {
      const response = await http.post<ItemEnvelope<DdexConfiguration>>(RESOURCE_BASE, input);
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DDEX_CONFIGURATIONS_QUERY_KEY });
    },
  });
}

export function useUpdateDdexConfiguration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (variables: { id: number; input: DdexConfigurationUpdateInput }) => {
      const response = await http.patch<ItemEnvelope<DdexConfiguration>>(
        `${RESOURCE_BASE}/${variables.id}`,
        variables.input,
      );
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DDEX_CONFIGURATIONS_QUERY_KEY });
    },
  });
}

export function useDeleteDdexConfiguration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await http.delete(`${RESOURCE_BASE}/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DDEX_CONFIGURATIONS_QUERY_KEY });
    },
  });
}
